<template>
  <div class="auth">
    <div class="auth__logo">
      <img src="@/assets/img/employee/logo/altezza-logo.svg" alt="Altezza Travel">
    </div>
    <h1 class="auth__title title">{{ $t('auth.welcome') }}</h1>
    <form v-if="!step_2" @submit.prevent="onCheckForm" class="auth__form">
      <app-form-group :label="$t('auth.login')" label-for="login">
        <app-input
          v-model="username"
          @change.native="$v.username.$touch()"
          :error="$v.username.$error"
          id="login"
          :placeholder="$t('auth.login')"
          name="login"
        />
        <template #error>
          <div v-if="$v.username.$dirty && !$v.username.required">{{ $t('auth.required') }}</div>
        </template>
      </app-form-group>
      <app-form-group :label="$t('auth.password')" label-for="password">
        <app-input
          v-model="password"
          @change.native="$v.password.$touch()"
          :error="$v.password.$error"
          :type="show_pass ? 'text' : 'password'"
          id="password"
          :placeholder="$t('auth.password')"
          name="password"
        />
        <template #right_place>
          <button
            @click="show_pass = !show_pass"
            type="button"
          >
            <svg v-if="show_pass" width="14" height="9" viewBox="0 0 14 9" fill="none"
                 xmlns="http://www.w3.org/2000/svg">
              <path
                d="M13.813 4.159C12.41 1.594 9.798 0 7 0S1.591 1.593.187 4.159L0 4.5l.187.341C1.59 7.406 4.202 9 7 9s5.409-1.593 6.813-4.159L14 4.5l-.187-.341ZM7.07 6.919c-1.377 0-2.497-1.184-2.497-2.64 0-1.455 1.12-2.64 2.497-2.64 1.377 0 2.497 1.185 2.497 2.64 0 1.456-1.12 2.64-2.497 2.64ZM1.502 4.5A6.76 6.76 0 0 1 3.82 2.236a4.144 4.144 0 0 0-.537 2.043c0 1.066.399 2.033 1.043 2.751A6.727 6.727 0 0 1 1.502 4.5Zm8.417 2.407a4.115 4.115 0 0 0 .937-2.628c0-.691-.166-1.342-.46-1.91a6.768 6.768 0 0 1 2.102 2.13 6.735 6.735 0 0 1-2.579 2.408Zm-1.866-2.68C8.053 4.843 7.58 5.34 7 5.34s-1.052-.498-1.052-1.112c0-.615.47-1.113 1.052-1.113.581 0 1.053.498 1.053 1.113Z"
                fill="#77B017"/>
            </svg>
            <svg v-if="!show_pass" width="14" height="9" viewBox="0 0 14 9" fill="none"
                 xmlns="http://www.w3.org/2000/svg">
              <path
                d="M13.813 4.159C12.41 1.594 9.798 0 7 0S1.591 1.593.187 4.159L0 4.5l.187.341C1.59 7.406 4.202 9 7 9s5.409-1.593 6.813-4.159L14 4.5l-.187-.341ZM7.07 6.919c-1.377 0-2.497-1.184-2.497-2.64 0-1.455 1.12-2.64 2.497-2.64 1.377 0 2.497 1.185 2.497 2.64 0 1.456-1.12 2.64-2.497 2.64ZM1.502 4.5A6.76 6.76 0 0 1 3.82 2.236a4.144 4.144 0 0 0-.537 2.043c0 1.066.399 2.033 1.043 2.751A6.727 6.727 0 0 1 1.502 4.5Zm8.417 2.407a4.115 4.115 0 0 0 .937-2.628c0-.691-.166-1.342-.46-1.91a6.768 6.768 0 0 1 2.102 2.13 6.735 6.735 0 0 1-2.579 2.408Zm-1.866-2.68C8.053 4.843 7.58 5.34 7 5.34s-1.052-.498-1.052-1.112c0-.615.47-1.113 1.052-1.113.581 0 1.053.498 1.053 1.113Z"
                fill="#BDBDBD"/>
            </svg>
          </button>
        </template>
        <template #error>
          <div v-if="$v.password.$dirty && !$v.password.required">{{ $t('auth.required') }}</div>
          <div v-if=" $v.password.$dirty && !$v.password.minLength">
            {{ $t('auth.shortPassword') }}
          </div>
        </template>
      </app-form-group>
      <router-link class="auth__forgot" :to="{name: 'forgot-password'}">
        {{ $t('auth.forgot') }}
      </router-link>
      <app-cells position="center" :indent="false">
        <app-button ref="submit" type="submit" :disabled="$v.username.$error || $v.password.$error">
          {{ $t('auth.logIn') }}
        </app-button>
      </app-cells>
    </form>
    <app-input
      v-if="step_2"
      @input.native="onPasswordChanged"
      v-model="otp_code"
      type="number"
      :placeholder="$t('auth.otp')"
    />
    <p v-if="loginError" class="auth__support">
      {{ $t('auth.support') }}
    </p>
  </div>
</template>

<script>
import { login } from '@/http'
import { required, minLength } from 'vuelidate/lib/validators'
import { setAuthTokens } from 'axios-jwt'
import { normalizeGroups, setIndexPageMobile } from '@/router/personalCabinet'

export default {
  name: 'Login',
  data() {
    return {
      username: null,
      password: null,
      show_pass: false,
      step_2: false,
      otp_code: null,
      loginError: false,
    }
  },
  validations: {
    username: {required},
    password: {required, minLength: minLength(4)},
  },
  methods: {
    completeLogin(data) {
      setAuthTokens({
        accessToken: data.access,
        refreshToken: data.refresh,
      })
      setIndexPageMobile(data.index_page_mobile)
      const groups = normalizeGroups(data.groups || data.roles || [])
      if (groups.length) this.$store.commit('setUserGroups', groups)
      this.$router.push({name: data.index_page_mobile || 'base'})
    },
    onCheckForm() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.$notify({
          type: 'info',
          title: this.$t('common.formInvalid'),
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.loginError = false
      this.$refs.submit.preload = true
      login({username: this.username, password: this.password})
        .then(res => {
          if (res.data.login) {
            this.completeLogin(res.data)
          } else {
            this.step_2 = true
          }
        })
        .catch(() => {
          this.loginError = true
          this.$refs.submit.preload = false
        })
    },
    onPasswordChanged() {
      if (this.otp_code.length === 6) {
        login({otp_code: this.otp_code, username: this.username, password: this.password})
          .then(res => {
            if (res.data.login) {
              this.completeLogin(res.data)
            }
          })
          .catch(() => {
            this.otp_code = null
            this.$refs.submit.preload = false
          })
      }
    },
  },
}
</script>

<style lang="sass" scoped>
.auth
  display: flex
  flex-direction: column
  align-items: center

.auth__logo
  flex-shrink: 0
  margin-bottom: 76px
  line-height: 0

.auth__form
  width: 100%
  max-width: 300px

.auth__title
  margin-bottom: 35px
  text-align: center

.auth__forgot
  display: block
  margin: -8px 0 22px
  color: $color-theme
  font-size: 13px
  text-align: right

.auth__support
  max-width: 300px
  margin-top: 18px
  color: $color-error
  font-size: 13px
  text-align: center
</style>
